import React, { useState } from 'react'
import {
    StyledLeasingListingFinanceInfo,
    StyledLeasingListingPriceInfo,
    StyledLeasingListingPricePerMonth,
    StyledLeasingListingPriceWrapper,
    StyledSearchListingContainer,
    StyledSearchListingDetails,
    StyledSearchListingDetailsSubTitle,
    StyledSearchListingDetailsTitle,
    StyledSearchListingLabelUL,
    StyledSearchListingTop,
    StyledWhatIsLeasingButton,
    StyledVehicleLeaseOptionPart,
} from './search-listing-details.styles'
import { StyledSearchListingLabel } from '../../patterns/search-listing/search-listing.styles'
import { ListingType } from '../../__generated__/fed-graphql-types'
import {
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_LeasingListing_finance_LeasingListingFinance,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_badges_Badge,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext,
} from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { LeasingInfoModal } from '../carousel-images/leasing-info-modal'
import { PortalComponent } from '../modals/portal'
import { focusOnElement } from '../../utils/focus-on-element'
import { useJourneyDataBuilder } from '../../hooks/use-journey-data-builder'

interface LeasingListingDetailsProps {
    advertId: string
    fpaUrl: string
    subtitle?: string | null
    title: string | null
    type?: ListingType
    badges?: ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_badges_Badge> | null
    finance?: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_LeasingListing_finance_LeasingListingFinance | null
    priceFormattedAndRounded?: string
    leasingListingRef?: HTMLDivElement
    trackingContext?: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext
}

export const LeasingListingDetails = ({
    advertId,
    fpaUrl,
    subtitle,
    title,
    type,
    badges,
    finance,
    priceFormattedAndRounded,
    leasingListingRef,
    trackingContext,
}: LeasingListingDetailsProps) => {
    const [showLeasingModal, setShowLeasingModal] = useState(false)

    const { journeyDataBuilder } = useJourneyDataBuilder({
        trackingContext,
        ref: leasingListingRef,
        type,
        advertId,
    })

    return (
        <StyledSearchListingContainer>
            <PortalComponent show={showLeasingModal}>
                <LeasingInfoModal
                    handleHideModal={() => {
                        setShowLeasingModal(false)
                        focusOnElement(`${advertId}-leasing-info-button`)
                    }}
                />
            </PortalComponent>
            <StyledSearchListingDetails>
                <div>
                    <StyledSearchListingTop>
                        <StyledSearchListingDetailsTitle
                            as="a"
                            data-testid="leasing-listing-title"
                            href={fpaUrl}
                            onClick={journeyDataBuilder}
                        >
                            {title}
                        </StyledSearchListingDetailsTitle>

                        {subtitle && (
                            <StyledSearchListingDetailsSubTitle as="p" data-testid="search-listing-subtitle">
                                {subtitle}
                            </StyledSearchListingDetailsSubTitle>
                        )}
                    </StyledSearchListingTop>
                    {badges && (
                        <StyledSearchListingLabelUL>
                            {badges.map((badge, i) => (
                                <StyledSearchListingLabel
                                    key={`featTab-${advertId}-${i}`}
                                    as="li"
                                    $type={badge.type}
                                    $badges
                                >
                                    {badge.displayText}
                                </StyledSearchListingLabel>
                            ))}
                        </StyledSearchListingLabelUL>
                    )}
                    {finance && (
                        <StyledLeasingListingFinanceInfo as="p" data-testid="search-listing-subtitle">
                            <StyledVehicleLeaseOptionPart>{finance.initialPayment}</StyledVehicleLeaseOptionPart>
                            {' initial payment. '}
                            <StyledVehicleLeaseOptionPart>{finance.termMonths}</StyledVehicleLeaseOptionPart>
                            {' month contract. '}
                            <StyledVehicleLeaseOptionPart>{finance.mileage}</StyledVehicleLeaseOptionPart>
                            {' miles p/a. '}
                            {finance.representativeExample}
                        </StyledLeasingListingFinanceInfo>
                    )}
                </div>
                <div>
                    <StyledLeasingListingPriceWrapper>
                        <StyledLeasingListingPriceInfo>{'from '}</StyledLeasingListingPriceInfo>
                        <StyledLeasingListingPricePerMonth>
                            {priceFormattedAndRounded}
                        </StyledLeasingListingPricePerMonth>
                        <StyledLeasingListingPriceInfo>{' / month'}</StyledLeasingListingPriceInfo>
                    </StyledLeasingListingPriceWrapper>

                    <StyledWhatIsLeasingButton
                        text={'What is leasing?'}
                        displayType={'tertiary'}
                        underline={true}
                        onInteraction={() => {
                            setShowLeasingModal(true)
                        }}
                        attributes={{
                            id: `${advertId}-leasing-info-button`,
                            'aria-label': `click for more leasing information about ${title}`,
                        }}
                    />
                </div>
            </StyledSearchListingDetails>
        </StyledSearchListingContainer>
    )
}
